import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { getGamesConfig } from '../../config/games';
import { FaCross, FaTrophy, FaArrowRight } from 'react-icons/fa';
import { GiCardRandom, GiAnglerFish, GiSwordWound } from 'react-icons/gi';
import Pokeball from '../Icons/Pokeball';
import apiService from '../../services/api';
import SEOHead from '../SEOHead/SEOHead';
import './Home.scss';

// Accent color per game (drives the card's subtle tint + icon + arrow).
// Kept local for now; will move into shared game config when other pages adopt the new theme.
const ACCENTS = {
  'aoe2-units': '#a855f7',
  'bible-books': '#f59e0b',
  'pokemon': '#ef4444',
  'slay-the-spire': '#10b981',
  'subnautica-creatures': '#06b6d4',
};

const ICONS = {
  FaCross,
  GiCardRandom,
  GiAnglerFish,
  GiSwordWound,
  Pokeball,
};

const Home = () => {
  const navigate = useNavigate();
  const [leaderboardData, setLeaderboardData] = useState([]);
  const [leaderboardLoading, setLeaderboardLoading] = useState(true);

  const games = getGamesConfig();

  useEffect(() => {
    const fetchLeaderboard = async () => {
      try {
        const response = await apiService.get('/api/leaderboard/real-time?limit=5');
        const data = await response.json();
        setLeaderboardData(data.users || []);
      } catch (error) {
        console.error('Error fetching leaderboard:', error);
        setLeaderboardData([]);
      } finally {
        setLeaderboardLoading(false);
      }
    };
    fetchLeaderboard();
  }, []);

  const renderGameIcon = (iconName) => {
    const IconComponent = ICONS[iconName];
    return IconComponent ? <IconComponent aria-hidden="true" /> : null;
  };

  const renderMedal = (rank) => {
    const cls = `home__medal home__medal--${rank === 1 ? 'gold' : rank === 2 ? 'silver' : rank === 3 ? 'bronze' : 'plain'}`;
    return <span className={cls}>{rank}</span>;
  };

  const renderLeaderboardRows = () => {
    if (leaderboardLoading) {
      return Array.from({ length: 5 }, (_, i) => (
        <div key={`skel-${i}`} className="home__lb-row home__lb-row--skeleton">
          <div className="home__lb-cell home__lb-cell--rank"><span className="home__lb-skel home__lb-skel--medal" /></div>
          <div className="home__lb-cell home__lb-cell--name"><span className="home__lb-skel" /></div>
          <div className="home__lb-cell"><span className="home__lb-skel" /></div>
          <div className="home__lb-cell"><span className="home__lb-skel" /></div>
          <div className="home__lb-cell"><span className="home__lb-skel" /></div>
          <div className="home__lb-cell"><span className="home__lb-skel" /></div>
        </div>
      ));
    }

    const rows = leaderboardData.slice(0, 5).map((player, index) => {
      const rank = index + 1;
      return (
        <div key={player.id} className="home__lb-row">
          <div className="home__lb-cell home__lb-cell--rank">{renderMedal(rank)}</div>
          <div className="home__lb-cell home__lb-cell--name">
            <button
              className="home__lb-username"
              onClick={() => navigate(`/profile/${player.username}`)}
            >
              {player.username}
            </button>
          </div>
          <div className="home__lb-cell home__lb-cell--accent">{player.current_streak}</div>
          <div className="home__lb-cell">{player.best_streak}</div>
          <div className="home__lb-cell">{player.total_daily_games_played}</div>
          <div className="home__lb-cell">{player.total_games_played}</div>
        </div>
      );
    });

    const empty = 5 - leaderboardData.length;
    for (let i = 0; i < empty; i++) {
      rows.push(
        <div key={`empty-${i}`} className="home__lb-row home__lb-row--empty">
          <div className="home__lb-cell home__lb-cell--rank">{renderMedal(leaderboardData.length + i + 1)}</div>
          <div className="home__lb-cell home__lb-cell--name">—</div>
          <div className="home__lb-cell">—</div>
          <div className="home__lb-cell">—</div>
          <div className="home__lb-cell">—</div>
          <div className="home__lb-cell">—</div>
        </div>
      );
    }
    return rows;
  };

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "WebSite",
    "name": "GuessNexus",
    "url": "https://guessnexus.com",
    "description": "Play daily guessing games from your favorite franchises. Test your knowledge with Slay the Spire cards, Bible books, Subnautica creatures, and more!",
    "publisher": {
      "@type": "Organization",
      "name": "GuessNexus",
      "url": "https://guessnexus.com",
      "logo": "https://guessnexus.com/logo-transparent.png"
    },
    "potentialAction": {
      "@type": "SearchAction",
      "target": { "@type": "EntryPoint", "urlTemplate": "https://guessnexus.com/solve/{search_term_string}" },
      "query-input": "required name=search_term_string"
    },
    "mainEntity": {
      "@type": "ItemList",
      "name": "Daily Challenge Games",
      "itemListElement": games.map((game, index) => ({
        "@type": "VideoGame",
        "position": index + 1,
        "name": game.title,
        "description": game.description,
        "url": `https://guessnexus.com/solve/${game.id}`,
        "applicationCategory": "Game",
        "genre": game.category || "Puzzle Game"
      }))
    }
  };

  return (
    <>
      <SEOHead
        title="GuessNexus"
        description="🎮 Challenge yourself with daily guessing games from your favorite franchises! Test your knowledge today - Slay the Spire cards, Bible books, Subnautica creatures & more. Start playing now!"
        canonicalUrl="https://guessnexus.com"
        structuredData={structuredData}
      />
      <div className="home">
        <div className="home__corner home__corner--tl" aria-hidden="true" />
        <div className="home__corner home__corner--br" aria-hidden="true" />

        <header className="home__hero">
          <img
            src="/logo-transparent.png"
            alt="GuessNexus"
            className="home__hero-logo"
            width={120}
            height={120}
            draggable={false}
          />
          <h1 className="home__hero-title">GUESS NEXUS</h1>
          <p className="home__hero-subtitle">Challenge yourself with daily word guessing games</p>
        </header>

        <main className="home__main">
          <section className="home__games" aria-label="Games">
            {games.map((game) => {
              const accent = ACCENTS[game.id] || '#64748b';
              return (
                <Link
                  key={game.id}
                  to={`/solve/${game.id}`}
                  className="home__card"
                  style={{ '--accent': accent }}
                  aria-label={`Play ${game.title}`}
                >
                  <div className="home__card-icon">{renderGameIcon(game.icon)}</div>
                  <div className="home__card-body">
                    <h2 className="home__card-title">{game.shortTitle || game.title}</h2>
                    <p className="home__card-desc">{game.description}</p>
                  </div>
                  <span className="home__card-cta" aria-hidden="true">
                    <FaArrowRight />
                  </span>
                </Link>
              );
            })}
          </section>

          <section className="home__leaderboard" aria-label="Leaderboard">
            <header className="home__lb-header">
              <FaTrophy className="home__lb-trophy" aria-hidden="true" />
              <h2 className="home__lb-title">Leaderboard</h2>
              <span className="home__lb-meta">Top 5 Players</span>
            </header>

            <div className="home__lb-table" role="table">
              <div className="home__lb-row home__lb-row--head" role="row">
                <div className="home__lb-cell home__lb-cell--rank" role="columnheader">Rank</div>
                <div className="home__lb-cell home__lb-cell--name" role="columnheader">Username</div>
                <div className="home__lb-cell" role="columnheader">Current</div>
                <div className="home__lb-cell" role="columnheader">Best</div>
                <div className="home__lb-cell" role="columnheader">Daily</div>
                <div className="home__lb-cell" role="columnheader">Total</div>
              </div>
              {renderLeaderboardRows()}
            </div>

            <button
              type="button"
              className="home__lb-view-all"
              onClick={() => navigate('/leaderboard')}
            >
              View full leaderboard
            </button>
          </section>
        </main>
      </div>
    </>
  );
};

export default Home;
